import React, { useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { Map, Target, Shield, FileSignature, Eye, Brain, BarChart3, Clock, Flame, CheckCircle, Check } from 'lucide-react';
import { trackAddToCart, trackInitiateCheckout } from '../utils/trackAddToCart';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-10% 0px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 30
  }} transition={{
    duration: 0.6,
    delay,
    ease: 'easeOut'
  }} data-id="element-69">
      {children}
    </motion.div>;
};
export function BonusesSection() {
  return <section className="w-full bg-[#050505] py-24 px-4 relative overflow-hidden" data-id="element-70">
      {/* Background Gradient */}
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505] pointer-events-none" data-id="element-71"></div>

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-72">
        {/* SECTION HEADER */}
        <div className="text-center mb-20 max-w-3xl mx-auto" data-id="element-73">
          <FadeIn data-id="element-74">
            <h2 className="font-serif text-3xl md:text-5xl text-[#f5f0e8] mb-6" data-id="element-75">
              Bonos (Valor{' '}
              <span className="line-through decoration-red-500/50 text-[#8a8578]" data-id="element-76">
                $47 USD
              </span>
              ) — <span className="text-[#d4af37]" data-id="element-77">Gratis Hoy</span>
            </h2>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-78">
            <p className="text-[#8a8578] text-lg leading-relaxed mb-8" data-id="element-79">
              Cuando obtienes Trading en Paz, no solo recibes el sistema mental
              (PP-7 + RI-3). Para que lo implementes sin fricción y empieces a
              notar control desde la primera sesión, hoy te incluyo 3 bonos
              diseñados para convertir disciplina en algo ejecutable.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-80">
            <div className="inline-block px-6 py-3 bg-[#0a0a0a] border border-[#222] rounded-full" data-id="element-81">
              <p className="text-sm md:text-base text-[#8a8578]" data-id="element-82">
                Valor total de los bonos:{' '}
                <span className="line-through decoration-red-500/50" data-id="element-83">
                  $47 USD
                </span>{' '}
                <span className="mx-2" data-id="element-84">|</span> Tu precio hoy:{' '}
                <span className="text-[#d4af37] font-bold" data-id="element-85">$0 (gratis)</span>
              </p>
            </div>
          </FadeIn>
        </div>

        {/* BONUS CARDS */}
        <div className="space-y-12 mb-24" data-id="element-86">
          {/* BONO #1 */}
          <FadeIn data-id="element-87">
            <div className="bg-[#0a0a0a] border border-[#222] rounded-xl overflow-hidden flex flex-col md:flex-row group hover:border-[#d4af37]/30 transition-colors duration-500" data-id="element-88">
              <div className="md:w-2/5 relative min-h-[300px] md:min-h-full bg-[#080808] flex items-center justify-center p-8" data-id="element-89">
                <div className="absolute inset-0 bg-[#d4af37]/5 blur-[40px] rounded-full" data-id="element-90"></div>
                <img src="https://cdn.magicpatterns.com/uploads/qaQvkXWJZ8RDR5y9YY3mdE/Bono_1_Imagen.png" alt="Anti-Autosabotaje" className="w-full h-auto rounded-lg border border-[#222]" loading="lazy" decoding="async" data-id="element-91" />
              </div>
              <div className="md:w-3/5 p-8 md:p-10 flex flex-col" data-id="element-92">
                <div className="flex flex-wrap items-center gap-3 mb-4" data-id="element-93">
                  <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] text-black text-xs font-bold tracking-wider" data-id="element-94">
                    BONO #1 (GRATIS)
                  </span>
                  <span className="text-xs text-[#8a8578]" data-id="element-95">
                    Valor: <span className="line-through" data-id="element-96">$17 USD</span> |{' '}
                    <span className="text-[#d4af37]" data-id="element-97">Hoy: GRATIS</span>
                  </span>
                </div>

                <h3 className="font-serif text-2xl text-[#f5f0e8] mb-4" data-id="element-98">
                  Anti-Autosabotaje: Mapa de Gatillos + Plan de Intervención
                </h3>

                <p className="text-[#8a8578] text-sm mb-4" data-id="element-99">
                  Este es el bono para el momento donde más te traicionas:
                  cuando tu mente empieza a negociar con la misma frase de
                  siempre…
                </p>

                <div className="pl-4 border-l-2 border-[#d4af37]/30 mb-6 italic text-[#d4af37]/80 text-sm space-y-1" data-id="element-100">
                  <p data-id="element-101">"Solo un trade más."</p>
                  <p data-id="element-102">"Solo para recuperar."</p>
                  <p data-id="element-103">"Solo porque está clarísimo."</p>
                </div>

                <p className="text-[#f5f0e8] font-medium mb-4" data-id="element-104">
                  Aquí no hay teoría. Hay intervención.
                </p>

                <div className="space-y-4 mb-6" data-id="element-105">
                  <h4 className="text-xs uppercase tracking-widest text-[#555]" data-id="element-106">
                    Qué te da:
                  </h4>
                  <ul className="space-y-3" data-id="element-107">
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-108">
                      <Map className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-109" />
                      <span data-id="element-110">
                        Un mapa visual de los 7 gatillos que te cuestan dinero y
                        paz (FOMO, venganza, euforia, aburrimiento, vergüenza,
                        comparación, fatiga)
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-111">
                      <Target className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-112" />
                      <span data-id="element-113">
                        Un plan para identificar tu gatillo dominante del día en
                        90 segundos
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-114">
                      <Shield className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-115" />
                      <span data-id="element-116">
                        La respuesta mecánica para cortar el impulso antes del
                        pico (sin debatir contigo)
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="mt-auto pt-6 border-t border-[#222]" data-id="element-117">
                  <p className="text-sm italic text-[#d4af37]" data-id="element-118">
                    <span className="font-bold not-italic text-[#555] mr-2" data-id="element-119">
                      Por qué importa:
                    </span>
                    "Porque el autosabotaje no se vence con ganas. Se vence con
                    un protocolo que se activa solo."
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* BONO #2 */}
          <FadeIn data-id="element-120">
            <div className="bg-[#0a0a0a] border border-[#222] rounded-xl overflow-hidden flex flex-col md:flex-row group hover:border-[#d4af37]/30 transition-colors duration-500" data-id="element-121">
              <div className="md:w-2/5 relative min-h-[300px] md:min-h-full bg-[#080808] flex items-center justify-center p-8" data-id="element-122">
                <div className="absolute inset-0 bg-[#d4af37]/5 blur-[40px] rounded-full" data-id="element-123"></div>
                <img src="https://cdn.magicpatterns.com/uploads/ooH1SwCackC2np9RuWDeDK/Bono_2.png" alt="Contrato del Ejecutor" className="w-full h-auto rounded-lg border border-[#222]" loading="lazy" decoding="async" data-id="element-124" />
              </div>
              <div className="md:w-3/5 p-8 md:p-10 flex flex-col" data-id="element-125">
                <div className="flex flex-wrap items-center gap-3 mb-4" data-id="element-126">
                  <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] text-black text-xs font-bold tracking-wider" data-id="element-127">
                    BONO #2 (GRATIS)
                  </span>
                  <span className="text-xs text-[#8a8578]" data-id="element-128">
                    Valor: <span className="line-through" data-id="element-129">$13 USD</span> |{' '}
                    <span className="text-[#d4af37]" data-id="element-130">Hoy: GRATIS</span>
                  </span>
                </div>

                <h3 className="font-serif text-2xl text-[#f5f0e8] mb-4" data-id="element-131">
                  Identidad Trader Rentable: El Contrato del Ejecutor (RI-3 en
                  14 días)
                </h3>

                <p className="text-[#8a8578] text-sm mb-4" data-id="element-132">
                  Este bono es confrontativo, porque es lo que casi nadie quiere
                  aceptar:
                </p>

                <div className="pl-4 border-l-2 border-[#d4af37]/30 mb-6 italic text-[#d4af37]/80 text-sm" data-id="element-133">
                  <p data-id="element-134">
                    "Si no está escrito, se negocia. Si se negocia, se rompe. Y
                    si se rompe, tu identidad se parte en dos."
                  </p>
                </div>

                <div className="space-y-4 mb-6" data-id="element-135">
                  <h4 className="text-xs uppercase tracking-widest text-[#555]" data-id="element-136">
                    Qué te da:
                  </h4>
                  <ul className="space-y-3" data-id="element-137">
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-138">
                      <FileSignature className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-139" />
                      <span data-id="element-140">
                        Un Contrato del Ejecutor (firmable) con reglas no
                        negociables para operar como profesional
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-141">
                      <Eye className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-142" />
                      <span data-id="element-143">
                        Enfoque brutal en ambiente (lo que disparas con tu
                        setup, redes, notificaciones, fatiga)
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-144">
                      <Brain className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-145" />
                      <span data-id="element-146">
                        El ciclo que te opera en cada sesión: Ambiente →
                        Pensamiento → Sentimiento → Acción → Resultado
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-147">
                      <BarChart3 className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-148" />
                      <span data-id="element-149">
                        Un sistema de evidencia diaria (60 segundos) que instala
                        identidad sin depender de motivación
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="mt-auto pt-6 border-t border-[#222]" data-id="element-150">
                  <p className="text-sm italic text-[#d4af37]" data-id="element-151">
                    <span className="font-bold not-italic text-[#555] mr-2" data-id="element-152">
                      Por qué importa:
                    </span>
                    "Porque el trader rentable no es el que sabe más. Es el que
                    ejecuta igual cuando gana, cuando pierde y cuando el ego
                    aprieta."
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* BONO #3 */}
          <FadeIn data-id="element-153">
            <div className="bg-[#0a0a0a] border border-[#222] rounded-xl overflow-hidden flex flex-col md:flex-row group hover:border-[#d4af37]/30 transition-colors duration-500" data-id="element-154">
              <div className="md:w-2/5 relative min-h-[300px] md:min-h-full bg-[#080808] flex items-center justify-center p-8" data-id="element-155">
                <div className="absolute inset-0 bg-[#d4af37]/5 blur-[40px] rounded-full" data-id="element-156"></div>
                <img src="https://cdn.magicpatterns.com/uploads/cpYUmPGveHC3u7DvfiMsDs/Bono_3.png" alt="Rutina PDF" className="w-full h-auto rounded-lg border border-[#222]" loading="lazy" decoding="async" data-id="element-157" />
              </div>
              <div className="md:w-3/5 p-8 md:p-10 flex flex-col" data-id="element-158">
                <div className="flex flex-wrap items-center gap-3 mb-4" data-id="element-159">
                  <span className="px-3 py-1 rounded-full bg-gradient-to-r from-[#b8942e] to-[#e8c84a] text-black text-xs font-bold tracking-wider" data-id="element-160">
                    BONO #3 (GRATIS)
                  </span>
                  <span className="text-xs text-[#8a8578]" data-id="element-161">
                    Valor: <span className="line-through" data-id="element-162">$51.000 COP</span> |{' '}
                    <span className="text-[#d4af37]" data-id="element-163">Hoy: GRATIS</span>
                  </span>
                </div>

                <h3 className="font-serif text-2xl text-[#f5f0e8] mb-4" data-id="element-164">
                  Rutina PDF: Inicio + En Caliente + Cierre de Sesión (3 minutos
                  para operar plano)
                </h3>

                <p className="text-[#8a8578] text-sm mb-4" data-id="element-165">
                  Si tu sesión empieza sin ritual, empieza con improvisación. Y
                  si termina sin cierre, termina con espiral.
                </p>

                <p className="text-[#f5f0e8] font-medium mb-4" data-id="element-166">
                  Este bono te entrega una rutina corta tipo SOP (procedimiento
                  profesional) para:
                </p>

                <div className="space-y-4 mb-6" data-id="element-167">
                  <h4 className="text-xs uppercase tracking-widest text-[#555]" data-id="element-168">
                    Qué te da:
                  </h4>
                  <ul className="space-y-3" data-id="element-169">
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-170">
                      <Clock className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-171" />
                      <span data-id="element-172">
                        Inicio en 90 segundos: semáforo + regla del día +
                        checklist
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-173">
                      <Flame className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-174" />
                      <span data-id="element-175">
                        Intervención en caliente en 45 segundos: pausa física +
                        respiración + regla mecánica
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-sm text-[#8a8578]" data-id="element-176">
                      <CheckCircle className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" data-id="element-177" />
                      <span data-id="element-178">
                        Cierre en 90 segundos: evidencia mínima + ajuste para
                        mañana (sin castigo)
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="mt-auto pt-6 border-t border-[#222]" data-id="element-179">
                  <p className="text-sm italic text-[#d4af37]" data-id="element-180">
                    <span className="font-bold not-italic text-[#555] mr-2" data-id="element-181">
                      Por qué importa:
                    </span>
                    "Porque el autosabotaje casi siempre ocurre cuando: entras
                    sin estado o te quedas emocionalmente abierto al cerrar."
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* PACKAGE SUMMARY */}
        <FadeIn data-id="element-182">
          <div className="max-w-2xl mx-auto bg-[#0a0a0a] border border-[#222] border-t-4 border-t-[#d4af37] rounded-xl p-8 mb-16 shadow-2xl" data-id="element-183">
            <h3 className="text-center font-serif text-2xl text-[#f5f0e8] mb-8" data-id="element-184">
              Resumen del paquete
            </h3>
            <ul className="space-y-4 mb-8" data-id="element-185">
              <li className="flex items-start gap-3 text-[#f5f0e8]" data-id="element-186">
                <Check className="w-5 h-5 text-[#d4af37] flex-shrink-0" data-id="element-187" />
                <span data-id="element-188">
                  <span className="font-bold" data-id="element-189">
                    Trading en Paz (PDF principal):
                  </span>{' '}
                  PP-7 + RI-3
                </span>
              </li>
              <li className="flex items-start gap-3 text-[#f5f0e8]" data-id="element-190">
                <Check className="w-5 h-5 text-[#d4af37] flex-shrink-0" data-id="element-191" />
                <span data-id="element-192">
                  <span className="font-bold" data-id="element-193">Bono #1:</span> Anti-Autosabotaje
                  (Mapa + Intervención)
                </span>
              </li>
              <li className="flex items-start gap-3 text-[#f5f0e8]" data-id="element-194">
                <Check className="w-5 h-5 text-[#d4af37] flex-shrink-0" data-id="element-195" />
                <span data-id="element-196">
                  <span className="font-bold" data-id="element-197">Bono #2:</span> Contrato del
                  Ejecutor (Identidad + Ambiente + Leyes)
                </span>
              </li>
              <li className="flex items-start gap-3 text-[#f5f0e8]" data-id="element-198">
                <Check className="w-5 h-5 text-[#d4af37] flex-shrink-0" data-id="element-199" />
                <span data-id="element-200">
                  <span className="font-bold" data-id="element-201">Bono #3:</span> Rutina de 3
                  minutos (Inicio/En caliente/Cierre)
                </span>
              </li>
            </ul>
            <div className="text-center pt-6 border-t border-[#222]" data-id="element-202">
              <p className="text-[#8a8578] text-sm md:text-base" data-id="element-203">
                Valor de bonos:{' '}
                <span className="line-through decoration-red-500/50" data-id="element-204">
                  $47 USD
                </span>{' '}
                <br className="md:hidden" data-id="element-205" />
                <span className="text-[#d4af37] font-bold ml-2" data-id="element-206">
                  Hoy: incluidos GRATIS con tu acceso
                </span>
              </p>
            </div>
          </div>
        </FadeIn>

        {/* BOTTOM CTA */}
        <FadeIn data-id="element-207">
          <div className="text-center max-w-2xl mx-auto" data-id="element-208">
            <p className="text-xl text-[#f5f0e8] mb-2 font-medium" data-id="element-209">
              Si estás cansado de saber qué hacer… y aún así no hacerlo, haz
              esto ahora:
            </p>
            <p className="text-[#8a8578] mb-8" data-id="element-210">
              Descarga Trading en Paz y recibe los 3 bonos gratis para ejecutar
              desde hoy.
            </p>

            <a href="https://pay.hotmart.com/I104339970M?checkoutMode=10" target="_blank" rel="noopener noreferrer" onClick={() => trackInitiateCheckout()} className="inline-block px-12 py-4 bg-gradient-to-r from-[#b8942e] via-[#d4af37] to-[#e8c84a] text-black font-bold text-lg tracking-wide rounded hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all duration-300 transform hover:-translate-y-1" data-id="element-211">
              QUIERO DEJAR DE SABOTEARME HOY
            </a>
          </div>
        </FadeIn>
      </div>
    </section>;
}